
import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Avatar,
  useTheme,

  CircularProgress,
  Tooltip,
} from "@mui/material";
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import DownloadService from "services/download-service";
import { formatNumber } from "utils/numberUtils";
// [NEW] Import the Upgrade Popup
import { usePlatformScout } from "context/PlatformScoutContext";
import UpgradePopup from "layouts/components/Upgrade";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo";
import { usePermissions } from "context"; // [NEW]

function SearchCard({
  platformIcon,
  verifiedIcon,
  downloadIcon,
  result,
  countLabel,
  isInSocialStash: initialInSocialStash,
  onViewInSocialStash,
  fetchUsageData,
  canDownload = true,
  featureName,
  planRequired,
  usageRaw, // [NEW] Usage Data
  isWorkflow = false, // [NEW] Context Flag
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const theme = useTheme();
  const { grey } = theme.palette;

  // [NEW] Use Global Context
  const {
    startDownload,
    downloadState
  } = usePlatformScout();

  const [isInSocialStash, setIsInSocialStash] = useState(initialInSocialStash);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showDemoPopup, setShowDemoPopup] = useState(false); // [NEW] Demo Popup State
  const [upgradeType, setUpgradeType] = useState("feature"); // [NEW] feature | usage

  // [NEW] RBAC State
  const [showRBAC, setShowRBAC] = useState(false);
  const { hasPermission, requiredRoles } = usePermissions();

  // Derived Global State for THIS Card
  const downloadKey = `${result.platform}_${result.id}`;
  const itemState = downloadState.items?.[downloadKey];

  const isGlobalDownloading = itemState?.isDownloading || false;
  const globalProgress = itemState?.progress || 0;
  const isGlobalComplete = itemState?.isComplete || false;

  // [NEW] Sync completion state (useEffect)
  useEffect(() => {
    if (isGlobalComplete) {
      setIsInSocialStash(true);
    }
  }, [isGlobalComplete]);

  const handleDownloadClick = async () => {
    if (isInSocialStash || isGlobalDownloading) return; // ⛔ prevent duplicate

    // [NEW] RBAC Check
    if (!hasPermission("stash:manage")) {
      setShowRBAC(true);
      return;
    }

    // [NEW] Live Demo Restriction
    const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
    if (isDemo) {
      setShowDemoPopup(true);
      return;
    }

    // [NEW] Entitlement & Usage Checks
    if (!canDownload) {
      setUpgradeType("feature");
      setShowUpgrade(true);
      return;
    }

    // [NEW] Check Usage Limits
    if (usageRaw) {
      const { profilesUsed, profilesLimit } = usageRaw;
      if (Number(profilesUsed) >= Number(profilesLimit)) {
        setUpgradeType("usage");
        setShowUpgrade(true);
        return;
      }
    }


    // Prepare Profile Data
    const profileData = {
      id: result.id,
      platform: result.platform,
      name: result.name,
      count: result.followers_count ?? result.subscribersCount ?? 0,
      creationDate: result.creationDate,
      profilePictureUrl: result.picture,
      link: result.link,
      verifiedStatus:
        result.verification_status === "verified" ||
        result.verification_status === "blue_verified",
      own: false,
      // clientId added by Context
    };

    // Trigger Global Download
    startDownload(profileData, fetchUsageData);
  };

  return (
    <>
      <Card
        onClick={handleDownloadClick}
        sx={{
          position: "relative",
          height: 200,
          width: 230,
          textAlign: "center",
          p: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          boxShadow: 2,
          overflow: "hidden",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-6px)",
            boxShadow: 6,
          },
          backgroundColor: "white",
          cursor: isInSocialStash ? "default" : "pointer", // 👁️ visual feedback
        }}
      >
        {/* Platform Icon */}
        <MDBox
          component="img"
          src={platformIcon}
          alt="Platform Icon"
          sx={{ position: "absolute", top: 8, left: 8, width: 24, height: 24 }}
        />

        {/* Profile Picture (stops card click) */}
        <MDBox
          component="a"
          href={result.link}
          target="_blank"
          rel="noopener noreferrer"
          position="relative"
          display="inline-block"
          onClick={(e) => e.stopPropagation()} // 🛑 prevent triggering card click
        >
          <Avatar
            src={result.picture || result.thumbnail || "https://via.placeholder.com/150"}
            sx={{ width: 80, height: 80, marginBottom: 1 }}
          />
          {(result.verification_status === "blue_verified" ||
            result.verification_status === "verified") && (
              <MDBox
                component="img"
                src={verifiedIcon}
                alt="Verified"
                sx={{ position: "absolute", bottom: 0, right: -8, width: 20, height: 20 }}
              />
            )}
        </MDBox>

        {/* Name */}
        <MDTypography
          variant="h6"
          fontWeight="bold"
          sx={{
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {result.name}
        </MDTypography>

        {/* Count Label */}
        <MDTypography variant="body2" color="text">
          {countLabel}: {formatNumber(result.followers_count || result.subscribersCount || 0)}
        </MDTypography>

        {/* Action Button Area */}
        <MDBox
          sx={{
            position: "absolute",
            bottom: 5,
            left: "50%",
            transform: "translateX(-50%)",
            width: "80%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {isInSocialStash ? (
            isWorkflow ? (
              <MDButton
                variant="contained"
                color="accent"
                disabled
                sx={{
                  whiteSpace: "nowrap",
                  px: 2,
                  textAlign: "center",
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.accent ? theme.palette.accent.main : "#FFB347",
                    color: "#000000 !important",
                    opacity: 1
                  }
                }}
              >
                Added
              </MDButton>
            ) : (
              <MDButton
                variant="contained"
                color="accent"
                onClick={(e) => {
                  e.stopPropagation(); // prevent card click
                  onViewInSocialStash();
                }}
                sx={{ whiteSpace: "nowrap", px: 2, textAlign: "center" }}
              >
                View in Social Stash
              </MDButton>
            )
          ) : isGlobalDownloading ? (
            <MDBox sx={{ position: "relative", display: "inline-flex" }}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={40}
                thickness={4}
                sx={{ color: "#d1f2f2", position: "absolute" }}
              />
              <CircularProgress
                variant="determinate"
                value={globalProgress}
                size={40}
                thickness={4}
                color="info"
              />
              <MDBox
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1, // ensure text overlays the circles
                }}
              >
                <MDTypography variant="caption" component="div" color="dark" fontWeight="bold">
                  {`${Math.round(globalProgress)}%`}
                </MDTypography>
              </MDBox>
            </MDBox>
          ) : (
            <Tooltip title="Download Profile" arrow>
              <MDBox
                bgColor="black"
                color="accent"
                coloredShadow="accent"
                borderRadius="50%"
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="1.5rem"
                height="1.5rem"
                // mt={-4}
                mb={1}
              >
                <DownloadForOfflineIcon
                  fontSize="large"
                  onClick={(e) => {
                    e.stopPropagation(); // avoid triggering card click twice
                    handleDownloadClick();
                  }}
                />
              </MDBox>
            </Tooltip>
          )}
        </MDBox>
      </Card>

      {/* [NEW] Upgrade Popup */}
      <UpgradePopup
        open={showUpgrade}
        onClose={(e) => {
          if (e) e.stopPropagation();
          setShowUpgrade(false);
        }}
        type={upgradeType} // "feature" or "usage"
        featureName={upgradeType === 'usage' ? "Tracked Profiles" : featureName}
        planRequired={planRequired}
        currentPlan={usageRaw?.currentPlan}
        usageLimit={usageRaw?.profilesLimit}
      />

      {/* [NEW] Live Demo Rules Popup */}
      <LiveDemoRestriction
        open={showDemoPopup}
        onClose={() => setShowDemoPopup(false)}
        featureName="Downloading a Profile"
        helpText="Please use the sample profiles provided in Social Stash."
      />

      {/* [NEW] RBAC Restriction Popup */}
      <UpgradePopup
        open={showRBAC}
        onClose={() => setShowRBAC(false)}
        type="rbac"
        featureName="Downloading Profiles"
        roleRequired={requiredRoles["stash:manage"] || "Manager"}
      />
    </>
  );
}

export default SearchCard;